import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import toRouteDocuments          from '@/TokamapEditor/helpers/toRouteDocuments';
import { documentConnectivity }  from '@tokamap/core';
import { loadCurrentDocuments }  from '@/TokamapEditor/stores/PreviewMapsStore';
import { pairMapsWithDocuments } from '@/TokamapEditor/stores/pairMapsWithDocuments';

/** Ce que les zones qui mènent à une autre carte relient, et ce qu'elles ratent — en libellés de cartes. */
export interface MapLinksReport {
	/** Groupes de cartes qui se rejoignent, le plus grand d'abord. Un seul groupe : tout se parcourt. */
	groups: string[][];

	/** Cartes vers lesquelles on peut aller sans pouvoir revenir. */
	oneWayLinks: { from: string; to: string }[];

	/** Cartes portant une zone qui mène à une carte qui n'existe plus. */
	brokenLinks: string[];
}

/**
 * Contrôle les liaisons entre les cartes du projet, avant de le publier. Il faut tous les documents
 * pour savoir ce qui se rejoint, d'où la même requête que l'aperçu.
 *
 * `null` quand il n'y a rien à dire : projet d'une seule carte, ou documents illisibles — annoncer
 * une carte isolée qu'on n'a pas su lire serait pire que se taire.
 */
export default async function checkMapLinks(): Promise<MapLinksReport | null> {
	const projectMaps = currentProjectEditorStore.get.mapCollection.models;

	// Avant la requête : un projet d'une seule carte n'a aucune liaison à contrôler.
	if (projectMaps.length < 2) return null;

	const documents = await loadCurrentDocuments();
	// Sans les thèmes : ce contrôle ne regarde que les liaisons entre cartes, pas leur rendu.
	const maps = pairMapsWithDocuments(projectMaps, documents.maps);

	if (maps.length < 2) return null;

	const labels = new Map(maps.map(map => [String(map.mapId), map.label]));
	const label = (documentId: string) => labels.get(documentId) ?? documentId;

	const connectivity = documentConnectivity(toRouteDocuments(maps));

	return {
		groups: connectivity.groups.map(group => group.map(label)),
		oneWayLinks: connectivity.oneWayLinks.map(link => ({
			from: label(link.documentId),
			to: label(link.targetDocumentId),
		})),
		// La carte visée n'existe plus : seule celle qui porte la zone est nommable, et elle peut en
		// porter plusieurs.
		brokenLinks: [...new Set(connectivity.brokenLinks.map(link => label(link.documentId)))],
	};
}

/** Vrai quand le contrôle n'a rien trouvé à signaler — voir `checkMapLinks`. */
export function hasNoLinkIssue(report: MapLinksReport): boolean {
	return report.groups.length === 1 && !report.oneWayLinks.length && !report.brokenLinks.length;
}
